/**
 * API Service para Imágenes de Mantenimientos (Posterior al trabajo)
 * 
 * Maneja todas las operaciones CRUD para imágenes asociadas a mantenimientos,
 * siguiendo la arquitectura estándar del proyecto con normalizedApiClient.
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';
import apiClient from './apiClient';
import ImageCompressionService from './imageCompressionService';
import { toast } from 'react-hot-toast';

/**
 * Obtiene todas las imágenes de un mantenimiento
 * @param {string|number} mantenimientoId - ID del mantenimiento
 * @param {string} tipo - Tipo de imagen opcional ('despues', 'proceso')
 * @returns {Promise<Array>} - Lista de imágenes
 */
export const obtenerImagenesMantenimiento = async (mantenimientoId, tipo = null) => {
  try {
    console.log('🖼️ [MantenimientoImagenesService] Obteniendo imágenes para mantenimiento:', mantenimientoId);
    
    if (!mantenimientoId) {
      return [];
    }
    
    const params = tipo ? { tipo } : {};
    const response = await apiHelpers.generic.get(API_ENDPOINTS.MANTENIMIENTOS.IMAGENES(mantenimientoId), { params });
    
    if (response.data) {
      const imagenesData = response.data.imagenes || response.data;
      console.log(`✅ [MantenimientoImagenesService] ${imagenesData.length} imágenes encontradas`);
      
      return imagenesData.map(imagen => ({
        id: imagen.id,
        tipo: imagen.tipo_imagen,
        nombre_archivo: imagen.nombre_archivo,
        url_storage: imagen.url_storage,
        thumbnail_url: imagen.thumbnail_url,
        descripcion: imagen.descripcion,
        orden: imagen.orden,
        tamaño_kb: imagen.tamaño_kb,
        dimensiones: {
          ancho: imagen.ancho_px,
          alto: imagen.alto_px
        },
        created_at: imagen.created_at,
        created_by: imagen.created_by
      }));
    }
    
    return [];
  } catch (error) {
    console.error(`❌ [MantenimientoImagenesService] Error al obtener imágenes de mantenimiento ${mantenimientoId}:`, error);
    throw error;
  }
};

/**
 * Obtiene imágenes organizadas por tipo
 * @param {string|number} mantenimientoId - ID del mantenimiento
 * @returns {Promise<Object>} - Objeto con imágenes organizadas por tipo
 */
export const obtenerImagenesOrganizadas = async (mantenimientoId) => {
  try {
    console.log('📊 [MantenimientoImagenesService] Organizando imágenes por tipo para mantenimiento:', mantenimientoId);
    
    const imagenes = await obtenerImagenesMantenimiento(mantenimientoId);
    
    return {
      despues: imagenes.filter(img => img.tipo === 'despues'),
      proceso: imagenes.filter(img => img.tipo === 'proceso'),
      total: imagenes.length
    };
  } catch (error) {
    console.error(`❌ [MantenimientoImagenesService] Error al organizar imágenes de mantenimiento ${mantenimientoId}:`, error);
    throw error;
  }
};

/**
 * Sube imagen completa (compresión + API backend)
 * @param {string|number} mantenimientoId - ID del mantenimiento
 * @param {File} archivo - Archivo de imagen
 * @param {string} tipo - Tipo de imagen ('despues', 'proceso')
 * @param {string} descripcion - Descripción opcional
 * @param {number} orden - Orden de la imagen
 * @returns {Promise<Object>} - Imagen subida
 */
export const subirImagen = async (mantenimientoId, archivo, tipo = 'despues', descripcion = '', orden = 0) => {
  try {
    console.log('🖼️ [MantenimientoImagenesService] Iniciando subida de imagen para mantenimiento:', mantenimientoId);

    if (!mantenimientoId || !archivo) {
      throw new Error('ID de mantenimiento y archivo son requeridos');
    }

    // Validar tipo de imagen
    if (!['despues', 'proceso'].includes(tipo)) {
      throw new Error('Tipo de imagen inválido. Use: despues, proceso');
    }

    // 1. Comprimir imagen
    const compresionResult = await ImageCompressionService.compressImage(archivo);
    
    // 2. Preparar FormData para envío a API
    const formData = new FormData();
    formData.append('imagen', compresionResult.compressedFile);
    formData.append('tipo', tipo);
    formData.append('descripcion', descripcion || '');
    formData.append('orden', orden.toString());
    formData.append('nombreArchivo', archivo.name);

    // 3. Para FormData necesitamos usar apiClient directamente
    // Los interceptors de autenticación se aplicarán automáticamente
    const response = await apiClient.post(API_ENDPOINTS.MANTENIMIENTOS.IMAGENES(mantenimientoId), formData, {
      headers: {
        // NO especificar Content-Type para FormData - el browser lo hace automáticamente
      }
    });

    if (response.data) {
      const imagenCreada = response.data.imagen || response.data;
      console.log('✅ [MantenimientoImagenesService] Imagen subida exitosamente:', imagenCreada.id);
      
      return {
        id: imagenCreada.id,
        tipo: imagenCreada.tipo_imagen,
        nombre_archivo: imagenCreada.nombre_archivo,
        url_storage: imagenCreada.url_storage,
        thumbnail_url: imagenCreada.thumbnail_url,
        descripcion: imagenCreada.descripcion,
        orden: imagenCreada.orden,
        tamaño_kb: imagenCreada.tamaño_kb,
        dimensiones: {
          ancho: imagenCreada.ancho_px,
          alto: imagenCreada.alto_px
        },
        created_at: imagenCreada.created_at,
        metadata: {
          originalSize: compresionResult.originalSize,
          compressedSize: compresionResult.compressedSize,
          compressionRatio: compresionResult.compressionRatio
        }
      };
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error('❌ [MantenimientoImagenesService] Error en subirImagen:', error);
    toast.error(`Error subiendo imagen: ${error.message}`);
    throw error;
  }
};

/**
 * Elimina una imagen específica
 * @param {string|number} mantenimientoId - ID del mantenimiento
 * @param {string|number} imagenId - ID de la imagen
 * @returns {Promise<boolean>} - Éxito de la eliminación
 */
export const eliminarImagen = async (mantenimientoId, imagenId) => {
  try {
    console.log(`🗑️ [MantenimientoImagenesService] Eliminando imagen ${imagenId} de mantenimiento ${mantenimientoId}`);
    
    if (!mantenimientoId || !imagenId) {
      throw new Error('ID de mantenimiento e imagen son requeridos');
    }
    
    const response = await apiHelpers.generic.delete(API_ENDPOINTS.MANTENIMIENTOS.IMAGEN_BY_ID(mantenimientoId, imagenId));
    
    if (response.data) {
      console.log('✅ [MantenimientoImagenesService] Imagen eliminada exitosamente:', imagenId);
      return true;
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error(`❌ [MantenimientoImagenesService] Error al eliminar imagen ${imagenId}:`, error);
    toast.error(`Error eliminando imagen: ${error.message}`);
    throw error;
  }
};

/**
 * Obtiene imágenes completas para incluir en PDF
 * @param {string|number} mantenimientoId - ID del mantenimiento
 * @param {string|number} citaId - ID de la cita (opcional para imágenes ANTES)
 * @returns {Promise<Object>} - Objeto con todas las imágenes organizadas
 */
export const obtenerImagenesParaPDF = async (mantenimientoId, citaId = null) => {
  try {
    console.log('📄 [MantenimientoImagenesService] Obteniendo imágenes para PDF:', mantenimientoId);
    
    const imagenes = {
      antes: [],
      despues: [],
      proceso: []
    };

    // Obtener imágenes posteriores del mantenimiento
    const imagenesMantenimiento = await obtenerImagenesMantenimiento(mantenimientoId);
    imagenes.despues = imagenesMantenimiento.filter(img => img.tipo === 'despues');
    imagenes.proceso = imagenesMantenimiento.filter(img => img.tipo === 'proceso');

    // Obtener imágenes ANTES de la cita (si se proporciona citaId)
    if (citaId) {
      const { obtenerImagenesCita } = await import('./citaImagenesApiService');
      imagenes.antes = await obtenerImagenesCita(citaId);
    }

    return imagenes;
  } catch (error) {
    console.error(`❌ [MantenimientoImagenesService] Error al obtener imágenes para PDF ${mantenimientoId}:`, error);
    throw error;
  }
};

/**
 * Obtiene estadísticas de imágenes para un mantenimiento
 * @param {string|number} mantenimientoId - ID del mantenimiento
 * @returns {Promise<Object>} - Estadísticas de imágenes
 */
export const obtenerEstadisticasMantenimiento = async (mantenimientoId) => {
  try {
    console.log('📊 [MantenimientoImagenesService] Obteniendo estadísticas de imágenes para mantenimiento:', mantenimientoId);
    
    // Obtener las imágenes primero
    const imagenes = await obtenerImagenesMantenimiento(mantenimientoId);
    
    const totalImagenes = imagenes.length;
    const imagenesDespues = imagenes.filter(img => img.tipo === 'despues').length;
    const imagenesProcess = imagenes.filter(img => img.tipo === 'proceso').length;
    const tamañoTotal = imagenes.reduce((sum, img) => sum + (img.tamaño_kb || 0), 0);
    
    return {
      total_imagenes: totalImagenes,
      imagenes_despues: imagenesDespues,
      imagenes_proceso: imagenesProcess,
      tamaño_total_kb: tamañoTotal,
      tamaño_total_mb: (tamañoTotal / 1024).toFixed(2),
      primera_imagen: totalImagenes > 0 ? imagenes[0].created_at : null,
      ultima_imagen: totalImagenes > 0 ? imagenes[imagenes.length - 1].created_at : null
    };
  } catch (error) {
    console.error(`❌ [MantenimientoImagenesService] Error al obtener estadísticas de mantenimiento ${mantenimientoId}:`, error);
    throw error;
  }
};

/**
 * Hook personalizado para usar el servicio de imágenes de mantenimientos
 */
export const useMantenimientoImagenesApiService = () => {
  return {
    obtenerImagenesMantenimiento,
    obtenerImagenesOrganizadas,
    subirImagen,
    eliminarImagen,
    obtenerImagenesParaPDF,
    obtenerEstadisticasMantenimiento
  };
};

// Mantener compatibilidad con implementación anterior (clase estática)
class MantenimientoImagenesApiService {
  static async obtenerImagenesMantenimiento(_session, mantenimientoId, tipo = null) {
    return obtenerImagenesMantenimiento(mantenimientoId, tipo);
  }
  
  static async obtenerImagenesOrganizadas(_session, mantenimientoId) {
    return obtenerImagenesOrganizadas(mantenimientoId);
  }
  
  static async subirImagen(_session, mantenimientoId, _empresaId, _sucursalId, archivo, tipo = 'despues', descripcion = '', orden = 0) {
    // Los parámetros session, empresaId y sucursalId se ignoran en la nueva API ya que se manejan automáticamente
    return subirImagen(mantenimientoId, archivo, tipo, descripcion, orden);
  }
  
  static async eliminarImagen(_session, _imagenId) {
    // Necesitamos el mantenimientoId para la nueva API, pero por compatibilidad mantenemos la signatura
    // Este método requerirá actualización en el código que lo use
    throw new Error('Método legacy: usar eliminarImagen(mantenimientoId, imagenId) en su lugar');
  }
  
  static async obtenerImagenesParaPDF(_session, mantenimientoId, citaId = null) {
    return obtenerImagenesParaPDF(mantenimientoId, citaId);
  }
  
  static async obtenerEstadisticasMantenimiento(_session, mantenimientoId) {
    return obtenerEstadisticasMantenimiento(mantenimientoId);
  }

  // Métodos adicionales que estaban en la implementación original
  static async reordenarImagenes(_session, _mantenimientoId, _nuevosOrdenes) {
    // Este método requeriría implementación en el backend
    throw new Error('Método no implementado en la nueva API');
  }
  
  static async actualizarDescripcion(_session, _imagenId, _nuevaDescripcion) {
    // Este método requeriría implementación en el backend
    throw new Error('Método no implementado en la nueva API');
  }
  
  static async eliminarTodasImagenes(_session, _mantenimientoId) {
    // Este método requeriría implementación en el backend
    throw new Error('Método no implementado en la nueva API');
  }
}

export default MantenimientoImagenesApiService;